import { useContext, useMemo } from "react";
import { DriverContext } from "@/context/DriverContext";
import { getShiftDisplayLabel } from "@/types";
import { commonShiftLabel } from "@/utils/operationShifts";

export type ShiftDisplay = {
  forOperations(code: string, operationIds: (number | null | undefined)[]): string;
  /** Para intercalar en una frase: el default va en minúscula, el nombre configurado tal cual. */
  inSentence(code: string, operationIds: (number | null | undefined)[]): string;
  forSvc(code: string, svcId: number | null | undefined): string;
};

/**
 * Resuelve el nombre de un turno (AM/PM) según la operación. Si no hay un nombre
 * común configurado devuelve el default ("Mañana"/"Tarde"). No tira sin provider.
 */
export const useShiftDisplay = (): ShiftDisplay => {
  const userScope = useContext(DriverContext)?.userScope ?? null;

  return useMemo(() => {
    const operations = userScope?.operations ?? [];
    const svcs = userScope?.svcs ?? [];

    // El scope cacheado puede no traer `shifts`: queda undefined.
    const shiftsOf = (operationId: number | null | undefined) =>
      operationId == null ? null : operations.find((o) => o.id === operationId)?.shifts;

    const forOperations = (
      code: string,
      operationIds: (number | null | undefined)[]
    ): string =>
      commonShiftLabel(code, operationIds.map(shiftsOf)) ?? getShiftDisplayLabel(code);

    return {
      forOperations,
      inSentence: (code, operationIds) =>
        commonShiftLabel(code, operationIds.map(shiftsOf)) ??
        getShiftDisplayLabel(code).toLowerCase(),
      forSvc: (code, svcId) => {
        const svc = svcId == null ? undefined : svcs.find((s) => s.id === svcId);
        return forOperations(code, [svc?.operationId]);
      },
    };
  }, [userScope]);
};
